import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import * as S from '../additional-providers-form/additional-providers-form.styles';
import * as CS from '../section.styles';
import { BaseButton } from '../../../base';
import { CreateClaimProvider, CreateClaimProviderType, SupportProvider } from '../../../../api';
import { Field, Formik, FormikProps } from 'formik';
import _ from 'lodash';

import { InputField } from '../../../base/form';
import { ProviderAutocomplete } from '../additional-providers-form/provider-autocomplete';
import { toProviderList, validateProviderFormValues } from '../../../../stores/claim-editor/claim-editor-rules';
import { fieldDomId } from '../../../../stores/claim-editor/claim-editor-sections';
import { useClaimEditorRevealFieldErrors } from '../use-claim-editor-form-validation';

interface ProviderFormValues {
    providerLookup?: string;
    providerId?: string;
    organization: string;
    first: string;
    last: string;
    suffix?: string;
    npi: string;
    phone: string;
    fax: string;
}

const initialValues: ProviderFormValues = {
    providerLookup: '',
    providerId: '',
    organization: '',
    first: '',
    last: '',
    suffix: '',
    npi: '',
    phone: '',
    fax: '',
};

const hasProviderIdentityChanged = (
    values: ProviderFormValues,
    selectedProviderValues?: ProviderFormValues | null,
): boolean => {
    if (!selectedProviderValues) return false;
    return (
        values.organization !== selectedProviderValues.organization ||
        values.first !== selectedProviderValues.first ||
        values.last !== selectedProviderValues.last ||
        values.suffix !== selectedProviderValues.suffix ||
        values.npi !== selectedProviderValues.npi ||
        values.phone !== selectedProviderValues.phone ||
        values.fax !== selectedProviderValues.fax
    );
};

interface Props {
    claimEditorStore: ClaimEditorStore;
    serviceIndex: number;
}

export const ServiceLineProvidersForm: React.FunctionComponent<Props> = observer(({ claimEditorStore, serviceIndex }) => {
    const [showRendering, setShowRendering] = useState(false);
    const [showReferring, setShowReferring] = useState(false);
    const [showSupervising, setShowSupervising] = useState(false);
    const [showOrdering, setShowOrdering] = useState(false);

    useEffect(() => {
        const svc: any = (claimEditorStore.services || [])[serviceIndex] || {};
        const types = toProviderList(svc.providers).map((p: any) => String(p?.type).toUpperCase());
        setShowRendering(types.includes('RENDERING'));
        setShowReferring(types.includes('REFERRING'));
        setShowSupervising(types.includes('SUPERVISING'));
        setShowOrdering(types.includes('ORDERING'));
    }, [claimEditorStore.services, serviceIndex]);

    return (
        <S.FormContainer>
            <CS.ButtonContainer>
                <BaseButton
                    variant="primary"
                    onClick={() => {
                        claimEditorStore.updateServiceLineProvider(serviceIndex, 'RENDERING', {
                            first: '',
                            last: '',
                            npi: '',
                            phone: '',
                            fax: '',
                            type: CreateClaimProviderType.RENDERING,
                        });
                        setShowRendering(true);
                    }}
                    disabled={showRendering}
                >
                    + Add Rendering
                </BaseButton>
                <BaseButton
                    variant="primary"
                    onClick={() => {
                        claimEditorStore.updateServiceLineProvider(serviceIndex, 'REFERRING', {
                            first: '',
                            last: '',
                            npi: '',
                            phone: '',
                            fax: '',
                            type: CreateClaimProviderType.REFERRING,
                        });
                        setShowReferring(true);
                    }}
                    disabled={showReferring}
                >
                    + Add Referring
                </BaseButton>
                <BaseButton
                    variant="primary"
                    onClick={() => {
                        claimEditorStore.updateServiceLineProvider(serviceIndex, 'SUPERVISING', {
                            first: '',
                            last: '',
                            npi: '',
                            phone: '',
                            fax: '',
                            type: CreateClaimProviderType.SUPERVISING,
                        });
                        setShowSupervising(true);
                    }}
                    disabled={showSupervising}
                >
                    + Add Supervising
                </BaseButton>
                <BaseButton
                    variant="primary"
                    onClick={() => {
                        claimEditorStore.updateServiceLineProvider(serviceIndex, 'ORDERING', {
                            first: '',
                            last: '',
                            npi: '',
                            phone: '',
                            fax: '',
                            type: CreateClaimProviderType.ORDERING,
                        });
                        setShowOrdering(true);
                    }}
                    disabled={showOrdering}
                >
                    + Add Ordering
                </BaseButton>
            </CS.ButtonContainer>

            {showRendering && (
                <ProviderBlock
                    title="Rendering"
                    type={CreateClaimProviderType.RENDERING}
                    serviceIndex={serviceIndex}
                    claimEditorStore={claimEditorStore}
                    onDelete={() => { claimEditorStore.deleteServiceLineProvider(serviceIndex, 'RENDERING'); setShowRendering(false); }}
                />
            )}
            {showReferring && (
                <ProviderBlock
                    title="Referring"
                    type={CreateClaimProviderType.REFERRING}
                    serviceIndex={serviceIndex}
                    claimEditorStore={claimEditorStore}
                    onDelete={() => { claimEditorStore.deleteServiceLineProvider(serviceIndex, 'REFERRING'); setShowReferring(false); }}
                />
            )}
            {showSupervising && (
                <ProviderBlock
                    title="Supervising"
                    type={CreateClaimProviderType.SUPERVISING}
                    serviceIndex={serviceIndex}
                    claimEditorStore={claimEditorStore}
                    onDelete={() => { claimEditorStore.deleteServiceLineProvider(serviceIndex, 'SUPERVISING'); setShowSupervising(false); }}
                />
            )}
            {showOrdering && (
                <ProviderBlock
                    title="Ordering"
                    type={CreateClaimProviderType.ORDERING}
                    serviceIndex={serviceIndex}
                    claimEditorStore={claimEditorStore}
                    onDelete={() => { claimEditorStore.deleteServiceLineProvider(serviceIndex, 'ORDERING'); setShowOrdering(false); }}
                />
            )}
        </S.FormContainer>
    );
});

const ProviderBlock: React.FunctionComponent<{
    title: string;
    type: CreateClaimProviderType;
    serviceIndex: number;
    claimEditorStore: ClaimEditorStore;
    onDelete: () => void;
}> = observer(({ title, type, serviceIndex, claimEditorStore, onDelete }) => {
    const validationSignal = claimEditorStore.validationSignal;
    const validationEvaluated = claimEditorStore.validationEvaluated;
    const formikRef = useRef<FormikProps<ProviderFormValues> | null>(null);
    const selectedProviderValuesRef = useRef<ProviderFormValues | null>(null);

    // Normalize enum or string to canonical uppercase string key
    const typeString = React.useMemo(() => {
        const maybeString = typeof type === 'string' ? type : (CreateClaimProviderType as any)[type];
        return String(maybeString).toUpperCase();
    }, [type]);

    const getInitialValues = useCallback((): ProviderFormValues => {
        const svc: any = (claimEditorStore.services || [])[serviceIndex] || {};
        const providers = toProviderList(svc.providers);
        const existingProvider = providers.find((p: any) => String(p?.type).toUpperCase() === typeString) as
            | CreateClaimProvider
            | undefined;
        
        if (existingProvider) {
            return {
                providerLookup:
                    [existingProvider.first, existingProvider.last].filter(Boolean).join(' ') ||
                    existingProvider.organization ||
                    '',
                providerId: (existingProvider as any).providerId || (existingProvider as any).id || '',
                organization: existingProvider.organization || '',
                first: existingProvider.first || '',
                last: existingProvider.last || '',
                suffix: existingProvider.suffix || '',
                npi: existingProvider.npi || '',
                phone: existingProvider.phone || '',
                fax: existingProvider.fax || '',
            };
        }
        return initialValues;
    }, [claimEditorStore.services, serviceIndex, title, typeString]);

    const providerFieldPrefix = React.useMemo(() => {
        const svc: any = (claimEditorStore.services || [])[serviceIndex] || {};
        const providers = toProviderList(svc.providers);
        const pIdx = providers.findIndex((p: any) => String(p?.type).toUpperCase() === typeString);
        if (pIdx >= 0) {
            return `services[${serviceIndex}].providers[${pIdx}]`;
        }
        // No backing array entry for this role (e.g. a type mismatch or an object-keyed shape
        // whose value lacks `type`). Falling back to a shared index here would give two
        // ProviderBlocks missing an entry at the same time IDENTICAL dom ids — breaking
        // scrollToField and sending jumps to the wrong block. Key by role instead so each
        // missing-entry block still gets a unique id.
        return `services[${serviceIndex}].providers.${typeString.toLowerCase()}`;
    }, [claimEditorStore.services, serviceIndex, typeString]);

    const convertToProvider = (values: ProviderFormValues): CreateClaimProvider => {
        return {
            providerId: values.providerId || undefined,
            organization: values.organization || undefined,
            first: values.first || undefined,
            last: values.last || undefined,
            suffix: values.suffix?.trim() ? values.suffix.trim() : undefined,
            npi: values.npi || undefined,
            phone: values.phone || undefined,
            fax: values.fax || undefined,
            type: typeString as any,
        };
    };

    const debouncedSave = useCallback(
        _.debounce(() => {
                const latestValues = formikRef.current?.values;
                if (!latestValues) return;
                const providerData = convertToProvider(latestValues);
                claimEditorStore.updateServiceLineProvider(serviceIndex, typeString as any, providerData);
            }, 500),
        [claimEditorStore, serviceIndex, typeString],
    );

    const persistProvider = useCallback(() => {
        const latestValues = formikRef.current?.values;
        if (!latestValues) return;
        const providerData = convertToProvider(latestValues);
        claimEditorStore.updateServiceLineProvider(serviceIndex, typeString as any, providerData);
    }, [claimEditorStore, serviceIndex, typeString]);


        useEffect(() => {
            return () => {
                debouncedSave.cancel();
            };
        }, [debouncedSave]);

    return (
        <CS.SectionContainer>
            <CS.SectionHeader>
                <CS.SectionTitle>{title}</CS.SectionTitle>
                <BaseButton variant="warning" onClick={onDelete}>✕ Delete {title} Provider</BaseButton>
            </CS.SectionHeader>

            <Formik
                innerRef={formikRef as any}
                initialValues={getInitialValues()}
                validate={values =>
                    validateProviderFormValues(values, {
                        isProviderIdActive: claimEditorStore.isProviderIdActive,
                    })
                }
                onSubmit={() => {
                    // No-op, we auto-save
                }}
                enableReinitialize
                validateOnMount={false}
                validateOnChange={true}
                validateOnBlur={false}
            >
                {formik => {
                    // Auto-save whenever values change
                    useEffect(() => {
                        debouncedSave();
                    }, [formik.values, debouncedSave]);

                    useEffect(() => {
                        if (!selectedProviderValuesRef.current && formik.values.providerId) {
                            selectedProviderValuesRef.current = { ...formik.values };
                        }
                    }, [formik.values]);

                    useEffect(() => {
                        if (!formik.values.providerId) return;
                        if (!hasProviderIdentityChanged(formik.values, selectedProviderValuesRef.current)) return;
                        formik.setFieldValue('providerId', undefined, false);
                    }, [
                        formik.values.providerId,
                        formik.values.organization,
                        formik.values.first,
                        formik.values.last,
                        formik.values.suffix,
                        formik.values.npi,
                        formik.values.phone,
                        formik.values.fax,
                    ]);

                    useClaimEditorRevealFieldErrors(formik, validationSignal, validationEvaluated, () => {
                        debouncedSave.cancel();
                        persistProvider();
                    });

                    return (
                        <>
                            <S.FormGrid>
                                <ProviderAutocomplete
                                    providerType={type}
                                    formik={formik}
                                    onSelect={(p: SupportProvider) => {
                                        const displayName =
                                            [p.first, p.last].filter(Boolean).join(' ') || p.organization || '';
                                        const newValues: ProviderFormValues = {
                                            providerLookup: displayName,
                                            providerId: p.id || '',
                                            organization: p.organization || '',
                                            first: p.first || '',
                                            last: p.last || '',
                                            suffix: p.suffix || '',
                                            npi: p.npi || '',
                                            phone: p.phone || '',
                                            fax: p.fax || '',
                                        };
                                        selectedProviderValuesRef.current = newValues;
                                        formik.setValues(newValues);
                                    }}
                                    onClear={() => {
                                        formik.setFieldValue('providerId', undefined);
                                        formik.setFieldValue('providerLookup', '');
                                    }}
                                    claimEditorStore={claimEditorStore}
                                />

                                <S.FormField id={fieldDomId(`${providerFieldPrefix}.organization`)}>
                                <S.Label required={!formik.values.last.trim() || !formik.values.first.trim()}>Organization Name</S.Label>
                                    <Field
                                        name="organization"
                                        placeholder="Organization Name"
                                        component={InputField}
                                    />
                                </S.FormField>

                                <S.FormField id={fieldDomId(`${providerFieldPrefix}.first`)}>
                                <S.Label required={!formik.values.organization.trim()}>First Name</S.Label>
                                    <Field name="first" placeholder="First Name" component={InputField} />
                                </S.FormField>

                                <S.FormField id={fieldDomId(`${providerFieldPrefix}.last`)}>
                                <S.Label required={!formik.values.organization.trim()}>Last Name</S.Label>
                                    <Field name="last" placeholder="Last Name" component={InputField} />
                                </S.FormField>

                                <S.FormField id={fieldDomId(`${providerFieldPrefix}.suffix`)}>
                                    <S.Label>Suffix</S.Label>
                                    <Field name="suffix" placeholder="Suffix" component={InputField} maxLength={10 as any} />
                                </S.FormField>

                                <S.FormField id={fieldDomId(`${providerFieldPrefix}.npi`)}>
                                    <S.Label required>NPI</S.Label>
                                    <Field name="npi" placeholder="NPI" component={InputField} maxLength={10 as any} />
                                </S.FormField>
                                <S.FormField>
                                    <S.Label>Phone</S.Label>
                                    <Field name="phone" placeholder="Phone" component={InputField} />
                                </S.FormField>
                                <S.FormField>
                                    <S.Label>Fax</S.Label>
                                    <Field name="fax" placeholder="Fax" component={InputField} />
                                </S.FormField>
                            </S.FormGrid>
                        </>
                    );
                }}
            </Formik>
        </CS.SectionContainer>
    );
});

export default ServiceLineProvidersForm;

